import {AlertTriangle, X} from 'lucide-react'
import {useI18n} from '@/i18n'
import {InfoBanner} from '@/components/shared/InfoBanner'

export interface BulkValidationErrorEntry {
    beneficiaryIdentityId: string
    beneficiaryDisplayName?: string
    errors: Array<{code: string; message: string}>
}

export interface BulkValidationErrorBannerProps {
    entries: BulkValidationErrorEntry[]
    /**
     * Maps a beneficiary identity UUID → display name. Resolved from the
     * wizard's selectedBeneficiaries chips. When a UUID is not found in the
     * map, the UUID is rendered as a fallback (still readable but uglier).
     */
    displayNameOf: (beneficiaryIdentityId: string) => string
    onDismiss: () => void
    'data-testid'?: string
}

/**
 * Atomic-failure error banner for the bulk access-request submission. Renders
 * one row per failing beneficiary by display name (not raw UUID, per spec
 * §6 + §8.3) with the human-readable error messages indented below. Dismissible
 * — the dismiss handler typically also re-enables the Submit button.
 */
export function BulkValidationErrorBanner({
    entries,
    displayNameOf,
    onDismiss,
    'data-testid': testId = 'bulk-validation-error-banner',
}: BulkValidationErrorBannerProps) {
    const i18n = useI18n()
    if (entries.length === 0) return null
    /* WHY: bulk submit fails as a single AR0019 envelope but the user-relevant
       reason is the per-beneficiary code (e.g. GV0014 restriction violation).
       Surface the first inner code as data-error-code AND co-render the
       error-banner testid so the e2e end-of-test surface scan
       (assertExpectedErrorsObserved) picks it up the same way it does for any
       single-error banner. Without this, a test that expectErrorCode("GV0014")
       on a bulk-validation path would always trip the end-of-test check. */
    const dominantCode = entries.find((e) => e.errors.length > 0)?.errors[0]?.code
    return (
        <InfoBanner variant="destructive" data-testid={testId} data-error-code={dominantCode}>
            <div className="flex items-start gap-2" role="alert" data-testid="error-banner" data-error-code={dominantCode}>
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5"/>
                <div className="flex-1 space-y-2">
                    <div className="font-semibold" data-testid={`${testId}-title`}>
                        {i18n.accessRequests.bulkValidationFailed}
                    </div>
                    <ul className="space-y-2" data-testid={`${testId}-list`}>
                        {entries.map((entry) => (
                            <li
                                key={entry.beneficiaryIdentityId}
                                data-testid={`${testId}-row`}
                                data-beneficiary-id={entry.beneficiaryIdentityId}
                            >
                                <div className="font-medium">{entry.beneficiaryDisplayName ?? displayNameOf(entry.beneficiaryIdentityId)}</div>
                                <ul className="ml-4 list-disc text-xs opacity-90">
                                    {entry.errors.map((err, idx) => (
                                        <li key={`${entry.beneficiaryIdentityId}-${idx}`}>
                                            {err.message}
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </div>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="shrink-0 rounded p-1"
                    aria-label={String(i18n.common.close)}
                    data-testid={`${testId}-dismiss`}
                >
                    <X className="h-4 w-4"/>
                </button>
            </div>
        </InfoBanner>
    )
}
